
import React, { useRef, useEffect } from 'react';
import { Card } from "@/components/ui/card";
import { useGame } from '../../contexts/GameContext';
import { skills } from '../../data/skills';
import SkillCategory from './SkillCategory';
import SkillConnections from './SkillConnections';

const SkillTree: React.FC = () => {
  const { skills: userSkills, experience } = useGame();
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  
  // Group skills by category
  const skillsByCategory = skills.reduce((acc, skill) => {
    if (!acc[skill.category]) {
      acc[skill.category] = [];
    }
    acc[skill.category].push(skill);
    return acc;
  }, {} as Record<string, typeof skills>);
  
  // Check if user has a skill
  const hasSkill = (skillId: string) => {
    return userSkills.some(skill => skill.id === skillId);
  };

  // Check if all prerequisites are met
  const prerequisitesMet = (skill: typeof skills[0]) => {
    if (!skill.prerequisites || skill.prerequisites.length === 0) return true;
    return skill.prerequisites.every(prereqId => hasSkill(prereqId));
  };
  
  // Check if user has enough experience for this skill
  const hasEnoughExperience = (skill: typeof skills[0]) => {
    return experience >= (skill.requiredExperience || 0);
  };

  // Get the status of a skill
  const getSkillStatus = (skill: typeof skills[0]) => {
    if (hasSkill(skill.id)) return 'acquired';
    if (!prerequisitesMet(skill)) return 'locked';
    if (!hasEnoughExperience(skill)) return 'insufficient-exp';
    return 'available';
  };
  
  // Update canvas size on window resize
  useEffect(() => {
    const handleResize = () => {
      const canvas = canvasRef.current;
      if (!canvas) return;
      
      const container = canvas.parentElement;
      if (!container) return;
      
      canvas.width = container.clientWidth;
      canvas.height = container.clientHeight;
    };
    
    window.addEventListener('resize', handleResize);
    // Initial setup
    setTimeout(handleResize, 100);
    
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  
  return (
    <div className="relative">
      <SkillConnections 
        canvasRef={canvasRef} 
        skills={skills}
        hasSkill={hasSkill}
      />
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 relative z-10">
        {Object.entries(skillsByCategory).map(([category, categorySkills]) => (
          <SkillCategory 
            key={category} 
            category={category} 
            skills={categorySkills}
            getSkillStatus={getSkillStatus}
            hasSkill={hasSkill}
            experience={experience}
          />
        ))}
      </div>
    </div>
  );
};

export default SkillTree;
